import { CircularProgress, Skeleton } from "@mui/material";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";
import usePagination from "@mui/material/usePagination";
import { keepPreviousData } from "@tanstack/react-query";
import HelpAndShare from "components/LayoutFront/HelpAndShare";
import Wrapper from "components/LayoutFront/Wrapper";
import AllNewsFilters from "components/templates/news/AllNewsFilters";
import NewsTabs from "components/templates/news/NewsTabs";
import Button from "components/templatesComponents/Button";
import CardVertical from "components/templatesComponents/CardVertical";
import Icon from "components/templatesComponents/Icon";
import Pagination from "components/templatesComponents/Pagination";
import TagDismissible from "components/templatesComponents/tagsDSFR/TagDismissible";
import TagsContainer from "components/templatesComponents/tagsDSFR/TagsContainer";
import TagsList from "components/templatesComponents/tagsDSFR/TagsList";
import TemplateBanner from "components/templatesComponents/TemplateBanner";
import { useFindHighlightNews, useFindPagesByTemplateKeys, useGetTags } from "hooks/page.hook";
import PropTypes from "prop-types";
import React, { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import scrollIntoView from "scroll-into-view-if-needed";
import { debounce, queryPageKey } from "utils/commonUtils";
import t from "utils/locales/translation.json";
import { createQueryParams, getQueryParams } from "utils/urlUtils";

const articlesPerPage = 10;

const AllNewsTemplate = (props) => {
  const { page: pageVersion } = props;
  const { title, shortDescription, contents, page, template } = pageVersion;
  const { siteName } = contents;
  const { lang = "FR" } = page;

  const history = useHistory();

  const { pathname, search } = useLocation();
  const queryParams = getQueryParams(search);
  const queryPage = useMemo(() => Number(queryParams?.[queryPageKey]) || 1, [queryParams]);
  const defaultSelectedTag = useMemo(() => queryParams?.tag || "", [queryParams]);

  const siteNameString = typeof siteName === "string" ? siteName : siteName?.props?.siteName;

  const profilTagsArray = useMemo(
    () =>
      (lang.toUpperCase() === "EN" ? process.env.profil_tags_en : process.env.profil_tags || "")
        .toLowerCase()
        .split(","),
    [lang],
  );

  const getInitialsTagsForm = React.useCallback(() => {
    if (defaultSelectedTag && !profilTagsArray.includes(defaultSelectedTag.toLowerCase())) {
      return [defaultSelectedTag];
    }
    return [];
  }, [defaultSelectedTag, profilTagsArray]);

  const getInitialsProfilTags = React.useCallback(() => {
    if (defaultSelectedTag && profilTagsArray.includes(defaultSelectedTag.toLowerCase())) {
      return [defaultSelectedTag];
    }
    return [];
  }, [defaultSelectedTag, profilTagsArray]);

  const [value, setValue] = useState("");
  const [searchValue, setSearchValue] = useState("");
  const [selectedTagsForm, setSelectedTagsForm] = useState(getInitialsTagsForm());
  const [selectedProfilTags, setSelectedProfilTags] = useState(getInitialsProfilTags());
  const [formDate, setFormDate] = useState({ startDate: "aaaa-mm-jj", endDate: "aaaa-mm-jj" });

  const { startDate, endDate } = formDate;

  const { data: d } = useGetTags({
    currentSiteName: siteNameString,
    lang,
  });

  const { aggregations } = d || {};
  const { tags: objectTags } = aggregations || {};
  const { buckets = [] } = objectTags || {};

  const allTags = React.useMemo(() => buckets?.map((bucket) => bucket.key), [buckets]);
  const profilTags = React.useMemo(
    () => allTags?.filter((b) => profilTagsArray.includes(b.toLowerCase())),
    [allTags, profilTagsArray],
  );
  const thematicTags = React.useMemo(
    () => allTags?.filter((b) => !profilTagsArray.includes(b.toLowerCase())),
    [allTags, profilTagsArray],
  );

  const selectedTags = useMemo(
    () => selectedProfilTags?.concat(selectedTagsForm),
    [selectedTagsForm, selectedProfilTags],
  );

  useEffect(() => {
    if (defaultSelectedTag) {
      setSelectedProfilTags(getInitialsProfilTags());
    }
  }, [getInitialsProfilTags, defaultSelectedTag]);

  useEffect(() => {
    if (defaultSelectedTag) {
      setSelectedTagsForm(getInitialsTagsForm());
    }
  }, [getInitialsTagsForm, defaultSelectedTag]);

  useEffect(() => {
    if (value && value.length > 2) {
      debounce(setSearchValue, 500)(value);
      if (queryPage !== 1) {
        history.push({
          pathname,
          search: `?${createQueryParams({ ...queryParams, [queryPageKey]: 1 })}`,
        });
      }
    } else {
      debounce(setSearchValue, 500)("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const validStartDate = useMemo(
    () =>
      !Number.isNaN(new Date(startDate).getTime()) && Date.parse(startDate) > 0
        ? new Date(startDate)?.toISOString()
        : "",
    [startDate],
  );

  const validEndDate = useMemo(
    () =>
      !Number.isNaN(new Date(endDate).getTime()) && Date.parse(endDate) > 0
        ? new Date(`${endDate}`)?.toISOString().replace("T00:00:00.000Z", "T23:59:59.000Z")
        : "",
    [endDate],
  );

  useEffect(() => {
    if ((selectedTags.length > 0 || validStartDate || validEndDate) && queryPage !== 1) {
      history.push({
        pathname,
        search: `?${createQueryParams({ ...queryParams, [queryPageKey]: 1 })}`,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTags, validStartDate, validEndDate]);

  const filters = useMemo(() => {
    const newFilters = [`page.lang||eq||${lang}`];
    if (Array.isArray(selectedTags) && selectedTags.length > 0) {
      selectedTags.forEach((selectedTag) => newFilters.push(`tags||cont||${selectedTag}`));
    }
    if (validStartDate && (!validEndDate || validStartDate > validEndDate) && Date.now() - Date.parse(validStartDate)) {
      newFilters.push(`page.firstPublishedDate||gte||${validStartDate}`);
    }
    if (!validStartDate && validEndDate) {
      newFilters.push(`page.firstPublishedDate||lte||${validEndDate}`);
    }
    if (validStartDate && validEndDate && validStartDate < validEndDate) {
      newFilters.push(`page.firstPublishedDate||between||${validStartDate},${validEndDate}`);
    }
    return newFilters;
  }, [lang, selectedTags, validEndDate, validStartDate]);

  const {
    data: highlightedNews = [],
    isFetching: isHighlightedFetching,
    isLoading: isHighlightedLoading,
  } = useFindHighlightNews(
    {
      siteName: siteNameString,
      search: searchValue,
      filters,
    },
    { placeholderData: keepPreviousData },
  );

  const allNewsFilters = useMemo(() => {
    if (Array.isArray(highlightedNews) && highlightedNews.length > 0) {
      return [...filters, `id||ne||${highlightedNews[0].id}`];
    }
    return filters;
  }, [filters, highlightedNews]);

  const offset = (queryPage - 1) * articlesPerPage;

  const { data, isFetching, isLoading } = useFindPagesByTemplateKeys(
    {
      templates: ["NEWS"],
      siteName: siteNameString,
      filters: allNewsFilters,
      page: queryPage,
      perPage: highlightedNews && queryPage === 1 ? articlesPerPage - highlightedNews.length : articlesPerPage,
      offset: highlightedNews && queryPage !== 1 ? offset - highlightedNews.length : offset,
      search: searchValue,
    },
    { placeholderData: keepPreviousData },
  );

  const { pages, total: subTotal = 0 } = data || {};

  const total = useMemo(() => subTotal + (highlightedNews?.length || 0), [subTotal, highlightedNews]);

  const count = useMemo(() => Math.ceil(total / articlesPerPage), [total]);

  const scrollSent = () => {
    scrollIntoView(document.getElementById("articleTitle"), {
      block: "center",
      inline: "center",
    });
  };

  const handleChangeDate = (e) => {
    const { name, value: dateValue } = e.target;
    setFormDate({ ...formDate, [name]: dateValue });
  };

  const handleChangePage = (event, newPage) => {
    history.push({
      pathname,
      search: `?${createQueryParams({
        ...queryParams,
        [queryPageKey]: newPage,
      })}`,
    });
    scrollSent();
  };

  const handleSelectTagsForm = (tags) => setSelectedTagsForm(tags);

  const handleDeleteTagsForm = (tag) => {
    setSelectedTagsForm(selectedTagsForm.filter((st) => st !== tag));
  };

  const handleSelectProfilTags = (tags) => setSelectedProfilTags(tags);

  const handleDeleteProfilTag = (tag) => {
    setSelectedProfilTags(selectedProfilTags.filter((st) => st !== tag));
  };

  const { items } = usePagination({
    count,
    page: queryPage,
    onChange: handleChangePage,
  });

  const noResultText = useMemo(() => {
    if (searchValue) {
      return `${t[lang].all_news.no_result_search_title} : « ${searchValue} »`;
    }
    if (filters?.length > 0) {
      return t[lang].all_news.no_news_title;
    }
    return t[lang].all_news.no_news;
  }, [lang, searchValue, filters]);

  const searchReinit = () => {
    setValue("");
    setSelectedTagsForm([]);
    setSelectedProfilTags([]);
    setFormDate({ startDate: "aaaa-mm-jj", endDate: "aaaa-mm-jj" });
  };

  const bo = useMemo(() => typeof title === "object", [title]);

  const hasPages = useMemo(
    () => (Array.isArray(pages) && pages.length > 0) || highlightedNews.length > 0,
    [pages, highlightedNews],
  );

  const hasFilters = useMemo(
    () => searchValue || selectedTags.length > 0 || startDate !== "aaaa-mm-jj" || endDate !== "aaaa-mm-jj",
    [endDate, searchValue, selectedTags.length, startDate],
  );

  return (
    <>
      <TemplateBanner bo={bo} template={template}>
        <Stack>
          <h1 id="articleTitle">{title}</h1>
          {shortDescription && <p className="fr-text--xl">{shortDescription}</p>}
        </Stack>
      </TemplateBanner>
      <Wrapper template={template} sx={{ px: { xs: 2, lg: 0 } }}>
        <NewsTabs currentPageVersion={pageVersion} />
        {typeof siteName === "object" && (
          <Grid container spacing={2} sx={{ justifyContent: "center", alignItems: "flex-start", pt: 2, px: 2 }}>
            <h6>Sélectionner un site : </h6>
            {siteName}
          </Grid>
        )}
        <Grid
          container
          columnSpacing={{ xs: 0, lg: 7 }}
          rowSpacing={{ xs: 2, lg: 0 }}
          sx={{ alignItems: "baseline", justifyContent: { xs: "center", lg: "space-between" } }}
        >
          <Grid size={{ xs: 12, lg: 4 }}>
            <Box sx={{ display: { lg: "none" }, mx: -2, mb: !hasPages || hasFilters ? 2 : 0 }}>
              <Box
                component="section"
                className="fr-accordion"
                sx={{ "&.fr-accordion .fr-collapse--expanded": { px: 2, pt: 3 } }}
              >
                <h3 className="fr-accordion__title">
                  <button
                    type="button"
                    className="fr-accordion__btn"
                    aria-expanded="false"
                    aria-controls="accordion-etapes"
                  >
                    <Icon iconDSFR="filter-line" size="sm" sx={{ mr: 1 }} />
                    {t[lang].search.filter_title}
                  </button>
                </h3>
                <Box className="fr-collapse" id="accordion-etapes">
                  <AllNewsFilters
                    value={value}
                    setValue={setValue}
                    profilTags={profilTags}
                    thematicTags={thematicTags}
                    selectedProfilTags={selectedProfilTags}
                    selectedTagsForm={selectedTagsForm}
                    startDate={startDate}
                    endDate={endDate}
                    validStartDate={validStartDate}
                    validEndDate={validEndDate}
                    handleSelectProfilTags={handleSelectProfilTags}
                    handleSelectTagsForm={handleSelectTagsForm}
                    handleChangeDate={handleChangeDate}
                    isMobile
                  />
                  <Box
                    component="button"
                    type="button"
                    className="fr-btn"
                    aria-controls={total > 0 ? "accordion-etapes" : undefined}
                    onClick={() => (total > 0 ? undefined : searchReinit())}
                    sx={{ justifyContent: "center", mt: 3, width: "100%" }}
                  >
                    {total > 0
                      ? `${t[lang].all_news.pre_display_results}${total}${t[lang].all_news.post_display_results}`
                      : `${t[lang].common.news.no_result} /${t[lang].all_news.reset}`}
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box sx={{ display: { xs: "none", lg: "block" } }}>
              <h6>{t[lang].search.filter_title}</h6>
              <AllNewsFilters
                value={value}
                setValue={setValue}
                profilTags={profilTags}
                thematicTags={thematicTags}
                selectedProfilTags={selectedProfilTags}
                selectedTagsForm={selectedTagsForm}
                startDate={startDate}
                endDate={endDate}
                validStartDate={validStartDate}
                validEndDate={validEndDate}
                handleSelectProfilTags={handleSelectProfilTags}
                handleSelectTagsForm={handleSelectTagsForm}
                handleChangeDate={handleChangeDate}
              />
              {(searchValue || selectedTags.length > 0 || startDate !== "aaaa-mm-jj" || endDate !== "aaaa-mm-jj") && (
                <Button outlined primary iconDSFR="close-circle-line" onClick={() => searchReinit()} sx={{ mt: 3 }}>
                  {t[lang].all_news.filters_button_label}
                </Button>
              )}
            </Box>
          </Grid>
          <Grid size={{ xs: 12, lg: 8 }}>
            <Stack spacing={3} sx={{ "& li": { m: 0 } }}>
              {!Number.isNaN(total) && !isLoading && !isHighlightedLoading ? (
                <Stack
                  role="status"
                  spacing={3}
                  direction="row"
                  sx={{
                    alignItems: "center",
                    mb: 3,

                    display: {
                      xs: !hasPages || hasFilters ? "flex" : "none",
                      lg: "flex",
                    },
                  }}
                >
                  <h4>{hasPages ? `${total} ${t[lang].all_news.results}` : noResultText}</h4>
                  {(isFetching || isHighlightedFetching) && <CircularProgress size={24} />}
                </Stack>
              ) : (
                <Skeleton variant="rounded" width={150} height={30} sx={{ display: { xs: "none", lg: "block" } }} />
              )}
              {selectedTags.length > 0 && (
                <TagsContainer>
                  <TagsList
                    Component={TagDismissible}
                    label="Profil : "
                    tags={selectedProfilTags}
                    handleDeleteTag={handleDeleteProfilTag}
                  />
                  <TagsList Component={TagDismissible} tags={selectedTagsForm} handleDeleteTag={handleDeleteTagsForm} />
                </TagsContainer>
              )}
              {(isLoading || isHighlightedLoading) && (
                <Grid container spacing={3}>
                  {[...Array(8).keys()].map((n) => (
                    <Grid key={n} size={{ xs: 12, sm: 6 }}>
                      <Skeleton variant="rectangular" width={305} height={350} />
                    </Grid>
                  ))}
                </Grid>
              )}
              {hasPages ? (
                <>
                  <Grid container spacing={3} className="fr-raw-list" component="ul">
                    {highlightedNews &&
                      queryPage === 1 &&
                      highlightedNews.map(
                        (highlight) =>
                          highlight.fullPath && (
                            <Grid key={highlight.id} component="li" size={{ xs: 12, sm: 6 }}>
                              <CardVertical page={highlight} isHighlighted />
                            </Grid>
                          ),
                      )}
                    {Array.isArray(pages) &&
                      pages.map((p) => (
                        <Grid key={p.id} component="li" size={{ xs: 12, sm: 6 }}>
                          <CardVertical page={p} />
                        </Grid>
                      ))}
                  </Grid>
                  {items?.length > 3 && (
                    <Grid container sx={{ justifyContent: "center", pt: 2 }}>
                      <Pagination
                        items={items}
                        handleChangePage={handleChangePage}
                        count={count}
                        queryParams={queryParams}
                      />
                    </Grid>
                  )}
                </>
              ) : (
                <>
                  {searchValue ? (
                    <ul>
                      <li className="fr-text--lg">{t[lang].common.search.no_result_message1}</li>
                      <li className="fr-text--lg">{t[lang].common.search.no_result_message2}</li>
                      <li className="fr-text--lg">{t[lang].common.search.no_result_message3}</li>
                    </ul>
                  ) : (
                    <ul>
                      <li className="fr-text--lg">{t[lang].common.search.no_result_message3}</li>
                      <li className="fr-text--lg">{t[lang].common.search.no_result_message4}</li>
                    </ul>
                  )}
                  <Button outlined primary iconDSFR="close-circle-line" onClick={() => searchReinit()}>
                    {t[lang].all_news.search_button_label}
                  </Button>
                </>
              )}
            </Stack>
          </Grid>
        </Grid>
      </Wrapper>
      <HelpAndShare alwaysDisplayShare template={template} wrapperProps={{ breakpoint: "xl" }} />
    </>
  );
};

AllNewsTemplate.propTypes = {
  page: PropTypes.shape({
    title: PropTypes.node,
    shortDescription: PropTypes.node,
    contents: PropTypes.shape(),
    page: PropTypes.shape(),
    template: PropTypes.string,
  }).isRequired,
};

export default AllNewsTemplate;
